import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import apiClient from '../services/api';
import authService from '../services/authService';
import { store } from '../store/store';
import { clearUser } from '../store/slices/userSlice';
import {
  isEmbeddedKioskWebView,
  isRestaurantPathRedirectableToKiosk,
  KIOSK_APP_STORAGE_KEY,
  KIOSK_ORDER_TYPES_STORAGE_KEY,
  KIOSK_ROLE_STORAGE_KEY,
  KIOSK_SESSION_STORAGE_KEY,
  KIOSK_TABLE_STORAGE_KEY,
  KIOSK_TABLE_NUMBER_STORAGE_KEY,
  kioskLoginPath,
  KIOSK_TABLE_DINEIN_FEEDBACK_LOGOUT_MS,
  parseRestaurantAppFromPath,
  readStoredKioskAppName,
  shouldRedirectRestaurantPathToKiosk,
} from '../components/Kiosk/kioskPaths';

export type KioskRole = 'LOBBY_KIOSK' | 'TABLE_KIOSK';

export type KioskOrderTypeCode = 'DINEIN' | 'TAKEAWAY';

export interface PepparKioskConfig {
  role?: KioskRole;
  orderTypes?: KioskOrderTypeCode[] | string[];
  payFirst?: boolean;
  sessionId?: string | number;
  tableId?: number | null;
  restaurantName?: string;
  device?: { role?: KioskRole };
  table?: { id?: number | null; number?: string | null; name?: string | null };
  restaurant?: { id?: number | string; name?: string; appName?: string };
}

export interface PepparKioskBridge {
  getConfig?: () => PepparKioskConfig | null | undefined;
  endSession?: (reason: string) => void;
  notifyPaymentActive?: (active: boolean) => void;
  notifyOrderComplete?: (payload: Record<string, unknown>) => void;
}

declare global {
  interface Window {
    PepparKiosk?: PepparKioskBridge;
  }
}

export interface KioskModeState {
  /** Kiosk shell UX (hide guest header/footer, use kiosk paths). */
  enabled: boolean;
  /** True when on `/kiosk/...` (nested layout already renders chrome). */
  onKioskRoute: boolean;
  /** True when shell is active but URL is still under `/restaurants/...`. */
  embeddedGuestSurface: boolean;
  appName: string | null;
  role: KioskRole;
  orderTypes: KioskOrderTypeCode[];
  payFirst: boolean;
  sessionId: string | null;
  tableId: number | null;
  /** Guest-facing table number (not DB id). */
  tableNumber: string | null;
  restaurantName: string | null;
  /** Table dine-in order is complete — guest should leave feedback before logout. */
  awaitingFeedbackPhase: boolean;
  endSession: (reason: string) => void;
  beginTableDineInFeedbackWait: (targetAppName: string) => void;
  resetKioskGuestSessionUi: () => void;
  kioskGuestLogout: (targetAppName: string, reason?: string) => void;
}

const DEFAULT_ORDER_TYPES: KioskOrderTypeCode[] = ['DINEIN', 'TAKEAWAY'];

function parseAppName(pathname: string): string | null {
  const match = pathname.match(/^\/kiosk\/([^/]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

function normalizeOrderTypes(raw: unknown): KioskOrderTypeCode[] {
  if (!Array.isArray(raw) || raw.length === 0) {
    return DEFAULT_ORDER_TYPES;
  }
  const mapped = raw
    .map((v) => String(v).toUpperCase())
    .filter((v): v is KioskOrderTypeCode => v === 'DINEIN' || v === 'TAKEAWAY');
  return mapped.length > 0 ? mapped : DEFAULT_ORDER_TYPES;
}

function readStoredRole(): KioskRole {
  try {
    const raw = sessionStorage.getItem(KIOSK_ROLE_STORAGE_KEY);
    if (raw === 'TABLE_KIOSK' || raw === 'LOBBY_KIOSK') {
      return raw;
    }
  } catch {
    /* ignore */
  }
  return 'LOBBY_KIOSK';
}

function readStoredSessionId(): string | null {
  try {
    return sessionStorage.getItem(KIOSK_SESSION_STORAGE_KEY);
  } catch {
    return null;
  }
}

function readStoredTableId(): number | null {
  try {
    const raw = sessionStorage.getItem(KIOSK_TABLE_STORAGE_KEY);
    if (!raw) return null;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function readStoredTableNumber(): string | null {
  try {
    const raw = sessionStorage.getItem(KIOSK_TABLE_NUMBER_STORAGE_KEY);
    return raw && raw.trim() ? raw.trim() : null;
  } catch {
    return null;
  }
}

function readStoredOrderTypes(): KioskOrderTypeCode[] {
  try {
    const raw = sessionStorage.getItem(KIOSK_ORDER_TYPES_STORAGE_KEY);
    if (!raw) return DEFAULT_ORDER_TYPES;
    return normalizeOrderTypes(JSON.parse(raw));
  } catch {
    return DEFAULT_ORDER_TYPES;
  }
}

function persistMeta(partial: {
  role?: KioskRole;
  sessionId?: string | number | null;
  tableId?: number | null;
  tableNumber?: string | null;
  orderTypes?: KioskOrderTypeCode[];
}): void {
  try {
    if (partial.role) {
      sessionStorage.setItem(KIOSK_ROLE_STORAGE_KEY, partial.role);
    }
    if (partial.sessionId != null && partial.sessionId !== '') {
      sessionStorage.setItem(KIOSK_SESSION_STORAGE_KEY, String(partial.sessionId));
    }
    if (partial.tableId != null) {
      sessionStorage.setItem(KIOSK_TABLE_STORAGE_KEY, String(partial.tableId));
    }
    if (partial.tableNumber != null && partial.tableNumber.trim()) {
      sessionStorage.setItem(KIOSK_TABLE_NUMBER_STORAGE_KEY, partial.tableNumber.trim());
    }
    if (partial.orderTypes) {
      sessionStorage.setItem(KIOSK_ORDER_TYPES_STORAGE_KEY, JSON.stringify(partial.orderTypes));
    }
  } catch {
    /* ignore */
  }
}

const KioskModeContext = createContext<KioskModeState | null>(null);

function readBridgeAppName(): string | null {
  try {
    const cfg = window.PepparKiosk?.getConfig?.();
    const raw = cfg?.restaurant?.appName;
    return raw ? String(raw) : null;
  } catch {
    return null;
  }
}

export const KioskModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const onKioskRoute = location.pathname.startsWith('/kiosk/');
  const restaurantApp = parseRestaurantAppFromPath(location.pathname);
  const onRedirectableRestaurant =
    restaurantApp != null && isRestaurantPathRedirectableToKiosk(location.pathname);
  const embedded = isEmbeddedKioskWebView();
  const embeddedGuestSurface = !onKioskRoute && onRedirectableRestaurant && embedded;
  const enabled =
    onKioskRoute ||
    (onRedirectableRestaurant &&
      restaurantApp != null &&
      (embedded || shouldRedirectRestaurantPathToKiosk(restaurantApp)));

  const appName =
    (onKioskRoute ? parseAppName(location.pathname) : null) ||
    restaurantApp ||
    readStoredKioskAppName() ||
    readBridgeAppName();

  const [role, setRole] = useState<KioskRole>(readStoredRole);
  const [orderTypes, setOrderTypes] = useState<KioskOrderTypeCode[]>(readStoredOrderTypes);
  const [payFirst, setPayFirst] = useState(true);
  const [sessionId, setSessionId] = useState<string | null>(readStoredSessionId);
  const [tableId, setTableId] = useState<number | null>(readStoredTableId);
  const [tableNumber, setTableNumber] = useState<string | null>(readStoredTableNumber);
  const [restaurantName, setRestaurantName] = useState<string | null>(null);
  const [awaitingFeedbackPhase, setAwaitingFeedbackPhase] = useState(false);
  const feedbackLogoutTimerRef = useRef<number | null>(null);
  const feedbackWaitStartedRef = useRef(false);

  const clearFeedbackLogoutTimer = useCallback(() => {
    if (feedbackLogoutTimerRef.current != null) {
      window.clearTimeout(feedbackLogoutTimerRef.current);
      feedbackLogoutTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    document.body.classList.add('peppar-kiosk-active');
    if (embeddedGuestSurface) {
      document.body.classList.add('peppar-kiosk-embedded-guest');
    }
    return () => {
      document.body.classList.remove('peppar-kiosk-active');
      document.body.classList.remove('peppar-kiosk-embedded-guest');
    };
  }, [embeddedGuestSurface, enabled]);

  useEffect(() => {
    if (!enabled || !appName) return;
    try {
      sessionStorage.setItem(KIOSK_APP_STORAGE_KEY, appName);
    } catch {
      /* ignore */
    }
  }, [enabled, appName]);

  useEffect(() => {
    if (!enabled && !embedded) return;
    const bridgeConfig = window.PepparKiosk?.getConfig?.();
    if (!bridgeConfig) return;

    const roleFromDevice = bridgeConfig.device?.role;
    const nextRole =
      bridgeConfig.role === 'TABLE_KIOSK' ||
      bridgeConfig.role === 'LOBBY_KIOSK' ||
      roleFromDevice === 'TABLE_KIOSK' ||
      roleFromDevice === 'LOBBY_KIOSK'
        ? (bridgeConfig.role || roleFromDevice) as KioskRole
        : undefined;
    const nextTypes = bridgeConfig.orderTypes
      ? normalizeOrderTypes(bridgeConfig.orderTypes)
      : undefined;

    if (nextRole) setRole(nextRole);
    if (nextTypes) setOrderTypes(nextTypes);
    if (typeof bridgeConfig.payFirst === 'boolean') setPayFirst(bridgeConfig.payFirst);
    else if (nextRole === 'TABLE_KIOSK') setPayFirst(false);
    else if (nextRole === 'LOBBY_KIOSK') setPayFirst(true);

    if (bridgeConfig.sessionId != null) setSessionId(String(bridgeConfig.sessionId));
    const tablePk =
      bridgeConfig.tableId != null
        ? Number(bridgeConfig.tableId)
        : bridgeConfig.table?.id != null
          ? Number(bridgeConfig.table.id)
          : null;
    if (tablePk != null && Number.isFinite(tablePk)) setTableId(tablePk);
    const tableNumRaw = bridgeConfig.table?.number;
    const tableNum =
      tableNumRaw != null && String(tableNumRaw).trim() ? String(tableNumRaw).trim() : null;
    if (tableNum) setTableNumber(tableNum);
    if (bridgeConfig.restaurantName || bridgeConfig.restaurant?.name) {
      setRestaurantName(bridgeConfig.restaurantName || bridgeConfig.restaurant?.name || null);
    }

    persistMeta({
      role: nextRole,
      sessionId: bridgeConfig.sessionId,
      tableId: tablePk,
      tableNumber: tableNum,
      orderTypes: nextTypes,
    });
  }, [embedded, enabled]);

  useEffect(() => {
    if (!onKioskRoute) return;
    const params = new URLSearchParams(location.search);
    const kt = params.get('kt');
    if (!kt) return;

    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiClient.get('/api/kiosk/session/exchange', {
          params: { kt },
        });
        if (cancelled || !data?.success) return;
        if (data.sessionId != null) {
          setSessionId(String(data.sessionId));
          persistMeta({ sessionId: data.sessionId });
        }
        if (data.tableId != null) {
          const tid = Number(data.tableId);
          setTableId(tid);
          const num =
            data.tableNumber != null && String(data.tableNumber).trim()
              ? String(data.tableNumber).trim()
              : null;
          if (num) setTableNumber(num);
          persistMeta({ tableId: tid, tableNumber: num });
        }
      } catch {
        /* invalid token — continue without session meta */
      } finally {
        if (cancelled) return;
        params.delete('kt');
        const nextSearch = params.toString();
        navigate(
          { pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '', hash: location.hash },
          { replace: true }
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [location.hash, location.pathname, location.search, navigate, onKioskRoute]);

  const endSession = useCallback((reason: string) => {
    try {
      window.PepparKiosk?.endSession?.(reason);
    } catch {
      /* bridge optional */
    }
  }, []);

  const resetKioskGuestSessionUi = useCallback(() => {
    clearFeedbackLogoutTimer();
    feedbackWaitStartedRef.current = false;
    setAwaitingFeedbackPhase(false);
  }, [clearFeedbackLogoutTimer]);

  const kioskGuestLogout = useCallback(
    (targetAppName: string, reason = 'POST_ORDER') => {
      clearFeedbackLogoutTimer();
      feedbackWaitStartedRef.current = false;
      setAwaitingFeedbackPhase(false);
      setSessionId(null);
      try {
        sessionStorage.removeItem(KIOSK_SESSION_STORAGE_KEY);
      } catch {
        /* ignore */
      }
      endSession(reason);
      store.dispatch(clearUser());
      void authService.logout().catch(() => undefined);
      navigate(kioskLoginPath(targetAppName), { replace: true });
    },
    [clearFeedbackLogoutTimer, endSession, navigate]
  );

  const beginTableDineInFeedbackWait = useCallback(
    (targetAppName: string) => {
      if (feedbackWaitStartedRef.current) {
        return;
      }
      feedbackWaitStartedRef.current = true;
      setAwaitingFeedbackPhase(true);
      clearFeedbackLogoutTimer();
      feedbackLogoutTimerRef.current = window.setTimeout(() => {
        kioskGuestLogout(targetAppName, 'POST_ORDER');
      }, KIOSK_TABLE_DINEIN_FEEDBACK_LOGOUT_MS);
    },
    [clearFeedbackLogoutTimer, kioskGuestLogout]
  );

  useEffect(() => () => clearFeedbackLogoutTimer(), [clearFeedbackLogoutTimer]);

  const value = useMemo<KioskModeState>(
    () => ({
      enabled: !!enabled,
      onKioskRoute,
      embeddedGuestSurface,
      appName,
      role,
      orderTypes,
      payFirst,
      sessionId,
      tableId,
      tableNumber,
      restaurantName,
      awaitingFeedbackPhase,
      endSession,
      beginTableDineInFeedbackWait,
      resetKioskGuestSessionUi,
      kioskGuestLogout,
    }),
    [
      enabled,
      onKioskRoute,
      embeddedGuestSurface,
      appName,
      role,
      orderTypes,
      payFirst,
      sessionId,
      tableId,
      tableNumber,
      restaurantName,
      awaitingFeedbackPhase,
      endSession,
      beginTableDineInFeedbackWait,
      resetKioskGuestSessionUi,
      kioskGuestLogout,
    ]
  );

  return <KioskModeContext.Provider value={value}>{children}</KioskModeContext.Provider>;
};

export function useKioskMode(): KioskModeState {
  const ctx = useContext(KioskModeContext);
  if (!ctx) {
    return {
      enabled: false,
      onKioskRoute: false,
      embeddedGuestSurface: false,
      appName: null,
      role: 'LOBBY_KIOSK',
      orderTypes: DEFAULT_ORDER_TYPES,
      payFirst: true,
      sessionId: null,
      tableId: null,
      tableNumber: null,
      restaurantName: null,
      awaitingFeedbackPhase: false,
      endSession: () => undefined,
      beginTableDineInFeedbackWait: () => undefined,
      resetKioskGuestSessionUi: () => undefined,
      kioskGuestLogout: () => undefined,
    };
  }
  return ctx;
}
